/**
 * Script files for the Szkriptek tabs (admin drawer, AI designer): reads picked or dropped files and
 * tells a ucon from a StyleScript by content - a StyleScript is a JSON array, a ucon a JSON object.
 * A `{ "ucon": …, "style": … }` pair (the shape SetScripts takes; members as JSON or as JSON text)
 * gives both.
 */

export type TScriptKind = 'ucon' | 'style';

export interface IScriptFile { name: string; kind: TScriptKind; value: unknown; }
export interface IScriptFileError { name: string; error: string; }

export const SCRIPT_TITLE: Record<TScriptKind, string> = { ucon: 'UCon szkript', style: 'Stílus szkript' };

export const formatJson = (value: unknown): string => JSON.stringify(value, null, 2);

const PAIR_KEYS: Record<string, TScriptKind> = { ucon: 'ucon', uconScript: 'ucon', style: 'style', styleScript: 'style' };

const isObject = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

const fits = (kind: TScriptKind, value: unknown) => (kind === 'style' ? Array.isArray(value) : isObject(value));

/** The scripts in one file's text; throws with the reason when it holds none. */
export function classifyScript(name: string, text: string): IScriptFile[] {
    let value: unknown;
    try {
        value = JSON.parse(text);
    } catch (e) {
        throw new Error(`nem érvényes JSON (${e instanceof Error ? e.message : String(e)})`);
    }
    if (Array.isArray(value)) {
        return [{ name, kind: 'style', value }];
    }
    if (!isObject(value)) {
        throw new Error('sem UCon (objektum), sem stílus szkript (tömb)');
    }
    const keys = Object.keys(value);
    if (keys.length === 0 || !keys.every((key) => key in PAIR_KEYS)) {
        return [{ name, kind: 'ucon', value }];
    }
    const scripts: IScriptFile[] = [];
    for (const key of keys) {
        const kind = PAIR_KEYS[key];
        let member = value[key];
        if (member == null) continue;
        if (typeof member === 'string') {
            try {
                member = JSON.parse(member);
            } catch {
                throw new Error(`a „${key}” nem érvényes JSON`);
            }
        }
        if (!fits(kind, member)) {
            throw new Error(`a „${key}” nem ${kind === 'style' ? 'tömb' : 'objektum'}`);
        }
        scripts.push({ name, kind, value: member });
    }
    if (!scripts.length) {
        throw new Error('üres szkriptpár');
    }
    return scripts;
}

export async function readScriptFiles(files: File[]): Promise<{ scripts: IScriptFile[]; errors: IScriptFileError[] }> {
    const scripts: IScriptFile[] = [];
    const errors: IScriptFileError[] = [];
    for (const file of files) {
        try {
            scripts.push(...classifyScript(file.name, await file.text()));
        } catch (e) {
            errors.push({ name: file.name, error: e instanceof Error ? e.message : String(e) });
        }
    }
    return { scripts, errors };
}
